import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
  FormGroup,
  CircularProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tab,
  Tabs,
  Paper,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  Upload as UploadIcon,
  Group as GroupIcon,
  Add as AddIcon,
  AccountTree as AccountTreeIcon,
  Info as InfoIcon,
  Download as DownloadIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useAuth } from '../../../../../auth/AuthContext';
import { auth } from '../../../../../firebase';
import { getApiBase } from '../../../../../utils/apiConfig';


const EnhancedStandardProcessor = () => {
  const { user } = useAuth();
  
  // Single school processing state
  const [ncessId, setNcessId] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [newTeamName, setNewTeamName] = useState('');
  const [createTeamIfMissing, setCreateTeamIfMissing] = useState(false);
  const [servicesToRun, setServicesToRun] = useState({
    esri: true,
    projections: true,
    nearby_schools: true,
    metrics: true,
    team_assignment: true,
    ai_summaries: true
  });
  const [forceReprocess, setForceReprocess] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingResults, setProcessingResults] = useState(null);

  // AI summary background status: null | 'generating' | 'complete' | 'timeout'
  const [aiSummaryStatus, setAiSummaryStatus] = useState(null);
  
  // Bulk processing state
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkData, setBulkData] = useState([]);
  const [bulkResults, setBulkResults] = useState(null);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [bulkRunAiSummaries, setBulkRunAiSummaries] = useState(true);
  const [bulkJobId, setBulkJobId] = useState(null);
  const [bulkJobStatus, setBulkJobStatus] = useState(null);
  const [bulkAiSummariesJobStatus, setBulkAiSummariesJobStatus] = useState(null);

  const MAX_BULK_SCHOOLS = 1000;
  
  // Teams state
  const [availableTeams, setAvailableTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);

  // Poll for AI summary completion when ai_summaries was requested and processing succeeded
  useEffect(() => {
    const uuid = processingResults?.school_uuid;
    const wasRequested = servicesToRun.ai_summaries;
    if (!uuid || !wasRequested || !processingResults?.overall_success) return;

    setAiSummaryStatus('generating');
    let done = false;

    const checkStatus = async () => {
      if (done) return;
      try {
        const authHeaders = await getAuthHeaders();
        const resp = await fetch(
          `${getApiBase()}/v2/schools/${uuid}/ai-summary/status`,
          { headers: authHeaders, credentials: 'include' }
        );
        if (resp.ok) {
          const data = await resp.json();
          if (data?.has_any_summaries) {
            done = true;
            setAiSummaryStatus('complete');
            clearInterval(intervalId);
            clearTimeout(timeoutId);
          }
        }
      } catch (err) {
        console.warn('AI summary status check failed:', err);
      }
    };

    const intervalId = setInterval(checkStatus, 15000);
    const timeoutId = setTimeout(() => {
      if (!done) {
        clearInterval(intervalId);
        setAiSummaryStatus('timeout');
      }
    }, 600000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [processingResults?.school_uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load available teams
  const loadTeams = useCallback(async () => {
    setTeamsLoading(true);
    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${getApiBase()}/v2/admin/teams?getAllTeams=true`, {
        headers: authHeaders,
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Teams API response:', data); // Debug logging
        setAvailableTeams(data.teams || []);
      } else {
        console.error('Teams API error:', response.status, response.statusText);
        const errorData = await response.text();
        console.error('Error details:', errorData);
      }
    } catch (error) {
      console.error('Failed to load teams:', error);
    } finally {
      setTeamsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  const availableServices = [
    { 
      key: 'esri', 
      label: 'ESRI Demographics', 
      description: 'Fetch demographic data for school location',
      dependencies: []
    },
    { 
      key: 'projections', 
      label: 'Enrollment Projections', 
      description: 'Generate future enrollment forecasts',
      dependencies: []
    },
    { 
      key: 'nearby_schools', 
      label: 'Nearby Schools', 
      description: 'Find and analyze nearby competing schools',
      dependencies: ['esri']
    },
    { 
      key: 'metrics', 
      label: 'School Metrics', 
      description: 'Calculate performance and trend metrics',
      dependencies: ['esri', 'nearby_schools', 'projections']
    },
    { 
      key: 'ai_summaries', 
      label: 'AI Summaries', 
      description: 'Generate comprehensive AI-powered analysis (all 3 drive times)',
      dependencies: ['esri', 'nearby_schools', 'projections', 'metrics']
    },
    { 
      key: 'team_assignment', 
      label: 'Team Assignment', 
      description: 'Assign school to selected team',
      dependencies: ['esri', 'nearby_schools', 'projections', 'metrics']
    }
  ];



  const validateNcesId = (id) => {
    const cleaned = id.replace(/[^0-9]/g, '');
    if (cleaned.length === 11) {
      return '0' + cleaned;
    } else if (cleaned.length === 12) {
      return cleaned;
    } else {
      throw new Error(`NCES ID must be 11 or 12 digits, got ${cleaned.length}: ${cleaned}`);
    }
  };

  const handleServiceToggle = (serviceKey) => {
    setServicesToRun(prev => {
      const newState = { ...prev, [serviceKey]: !prev[serviceKey] };
      
      // Auto-enable dependencies if service is enabled
      if (newState[serviceKey]) {
        const service = availableServices.find(s => s.key === serviceKey);
        if (service) {
          service.dependencies.forEach(dep => {
            newState[dep] = true;
          });
        }
      }
      
      return newState;
    });
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      parseExcelFile(file);
    }
  };

  const parseExcelFile = async (file) => {
    try {
      // For now, we'll assume CSV format. In production, you'd use a library like xlsx
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      const header = lines[0].toLowerCase();
      
      if (!header.includes('nces') || !header.includes('team')) {
        throw new Error('Excel file must have columns for NCES ID and Team Name');
      }
      
      const data = [];
      for (let i = 1; i < lines.length; i++) {
        const parts = lines[i].split(',').map(p => p.trim().replace(/"/g, ''));
        if (parts.length >= 2) {
          try {
            const validatedNces = validateNcesId(parts[0]);
            data.push({
              nces_id: validatedNces,
              team_name: parts[1],
              original_nces: parts[0]
            });
          } catch (error) {
            console.warn(`Invalid NCES ID in row ${i + 1}: ${parts[0]}`);
          }
        }
      }
      
      setBulkData(data);
    } catch (error) {
      alert(`Error parsing file: ${error.message}`);
    }
  };

  const handleProcessStandard = async () => {
    if (!ncessId.trim()) {
      alert('Please enter an NCES ID');
      return;
    }

    let validatedNces;
    try {
      validatedNces = validateNcesId(ncessId);
    } catch (error) {
      alert(error.message);
      return;
    }

    const teamName = selectedTeam === 'new' ? newTeamName.trim() : selectedTeam;
    
    if (servicesToRun.team_assignment && !teamName) {
      alert('Please select a team or enter a new team name');
      return;
    }

    setIsProcessing(true);
    setProcessingResults(null);
    setAiSummaryStatus(null);

    try {
      const authHeaders = await getAuthHeaders();
      const selectedServices = Object.keys(servicesToRun).filter(key => servicesToRun[key]);

      const response = await fetch(`${getApiBase()}/v2/cloud-run/school/process-enhanced`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        credentials: 'include',
        body: JSON.stringify({
          nces_id: validatedNces,
          services: selectedServices,
          force_reprocess: forceReprocess,
          team_name: teamName || null,
          create_team_if_missing: createTeamIfMissing || selectedTeam === 'new'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Processing failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      setProcessingResults(result);
      
      // Reload teams if a new one was created
      if (result.service_results?.team_assignment?.assignment_created) {
        loadTeams();
      }

    } catch (error) {
      console.error('Enhanced processing failed:', error);
      setProcessingResults({
        overall_success: false,
        error: error.message,
        processing_completed_at: new Date().toISOString()
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Poll bulk job status when a job is active
  useEffect(() => {
    if (!bulkJobId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const authHeaders = await getAuthHeaders();
        const res = await fetch(`${getApiBase()}/v2/bulk/jobs/${bulkJobId}/status`, {
          headers: authHeaders,
          credentials: 'include'
        });
        if (cancelled || !res.ok) return;
        const data = await res.json();
        setBulkJobStatus(data);

        let aiStatus = null;
        if (data.ai_summaries_job_id) {
          const aiRes = await fetch(`${getApiBase()}/v2/bulk/jobs/${data.ai_summaries_job_id}/status`, {
            headers: authHeaders,
            credentials: 'include'
          });
          if (aiRes.ok) {
            aiStatus = await aiRes.json();
            setBulkAiSummariesJobStatus(aiStatus);
          }
        }

        const mainActive = data.status && ['created', 'processing'].includes(data.status);
        const aiActive = aiStatus?.status && ['created', 'processing'].includes(aiStatus.status);
        if (mainActive || aiActive) {
          setTimeout(poll, 1500);
          return;
        }

        if (data.status === 'completed') {
          setBulkResults({
            overall_success: (data.progress?.failed || 0) === 0 && (data.validation_errors?.length || 0) === 0,
            bulk_processing_completed_at: data.completed_at || new Date().toISOString(),
            schools_processed: Array.from({ length: data.progress?.completed || 0 }, (_, i) => ({})),
            schools_failed: (data.failed_schools || []).map((f) => ({ nces_id: f.school_data?.nces_id, error: f.error })),
            validation_errors: data.validation_errors || []
          });
          setIsBulkProcessing(false);
          loadTeams();
        } else if (data.status === 'failed' || data.status === 'cancelled') {
          setBulkResults({
            overall_success: false,
            error: data.status === 'cancelled' ? 'Job cancelled' : 'Job failed',
            bulk_processing_completed_at: new Date().toISOString(),
            schools_processed: [],
            schools_failed: data.failed_schools || [],
            validation_errors: data.validation_errors || []
          });
          setIsBulkProcessing(false);
        }
      } catch (e) {
        if (!cancelled) console.error('Bulk job poll error:', e);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [bulkJobId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBulkProcess = async () => {
    if (bulkData.length === 0) {
      alert('Please upload a valid Excel file with school data');
      return;
    }
    if (bulkData.length > MAX_BULK_SCHOOLS) {
      alert(`Maximum ${MAX_BULK_SCHOOLS} schools per job. Please split your file.`);
      return;
    }

    setIsBulkProcessing(true);
    setBulkResults(null);
    setBulkJobStatus(null);
    setBulkAiSummariesJobStatus(null);

    try {
      const authHeaders = await getAuthHeaders();
      const schools = bulkData.map((s) => ({ nces_id: s.nces_id, team_name: s.team_name || '' }));

      const response = await fetch(`${getApiBase()}/v2/bulk/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({
          job_type: 'standard_processing',
          schools,
          options: {
            chunk_size: 5,
            force_reprocess: forceReprocess,
            create_teams_if_missing: createTeamIfMissing,
            run_ai_summaries: bulkRunAiSummaries
          }
        })
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || `Bulk job failed: ${response.status}`);
      }

      setBulkJobId(result.job_id);
    } catch (error) {
      console.error('Bulk processing failed:', error);
      setBulkResults({
        overall_success: false,
        error: error.message,
        bulk_processing_completed_at: new Date().toISOString(),
        schools_processed: [],
        schools_failed: [],
        validation_errors: []
      });
      setIsBulkProcessing(false);
    }
  };

  const cancelBulkJob = async () => {
    if (!bulkJobId) return;
    try {
      const authHeaders = await getAuthHeaders();
      await fetch(`${getApiBase()}/v2/bulk/jobs/${bulkJobId}/cancel`, {
        method: 'POST',
        headers: authHeaders,
        credentials: 'include'
      });
      setBulkJobStatus((prev) => (prev ? { ...prev, status: 'cancelled' } : null));
      setIsBulkProcessing(false);
    } catch (e) {
      console.error('Cancel bulk job error:', e);
    }
  };

  // Per-school status for the current bulk job (main + AI) so users can see each school's completion
  const bulkSchoolRows = useMemo(() => {
    const rows = [];
    const main = bulkJobStatus;
    const ai = bulkAiSummariesJobStatus;
    const completedList = main?.completed_schools || [];
    const failedList = main?.failed_schools || [];
    const ncesToMain = new Map();
    completedList.forEach((entry) => {
      const nces = entry.school_data?.nces_id != null ? String(entry.school_data.nces_id) : null;
      if (nces) ncesToMain.set(nces, { status: 'completed', school_uuid: entry.school_uuid });
    });
    failedList.forEach((entry) => {
      const nces = entry.school_data?.nces_id != null ? String(entry.school_data.nces_id) : null;
      if (nces) ncesToMain.set(nces, { status: 'failed', error: entry.error });
    });
    const aiCompletedUuids = new Set(
      (ai?.completed_schools || []).map((e) => e.school_data?.school_uuid || e.school_uuid).filter(Boolean)
    );
    const aiFailedUuids = new Set(
      (ai?.failed_schools || []).map((e) => e.school_data?.school_uuid || e.school_uuid).filter(Boolean)
    );
    const hasAi = Boolean(main?.ai_summaries_job_id);
    (bulkData || []).forEach((school) => {
      const nces = String(school.nces_id ?? '');
      const mainInfo = ncesToMain.get(nces);
      const mainStatus = mainInfo?.status || (main && main.status !== 'completed' ? 'in_progress' : 'pending');
      const schoolUuid = mainInfo?.status === 'completed' ? mainInfo.school_uuid : null;
      let aiStatus = null;
      if (hasAi && schoolUuid) {
        if (aiCompletedUuids.has(schoolUuid)) aiStatus = 'completed';
        else if (aiFailedUuids.has(schoolUuid)) aiStatus = 'failed';
        else aiStatus = ai?.status === 'completed' ? 'pending' : 'in_progress';
      }
      rows.push({
        nces_id: nces,
        team_name: school.team_name || '',
        mainStatus,
        mainError: mainInfo?.error,
        aiStatus,
      });
    });
    return rows;
  }, [bulkData, bulkJobStatus, bulkAiSummariesJobStatus]);

  const getAuthHeaders = async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      console.warn('No authenticated user found');
      return {};
    }
    try {
      const token = await firebaseUser.getIdToken();
      return { 'Authorization': `Bearer ${token}` };
    } catch (error) {
      console.error('Error getting auth token:', error);
      return {};
    }
  };

  const createSampleTeams = async () => {
    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${getApiBase()}/v2/cloud-run/debug/create-sample-teams`, {
        method: 'POST',
        headers: authHeaders,
        credentials: 'include'
      });
      
      const result = await response.json();
      console.log('Sample teams result:', result);
      
      if (response.ok) {
        alert(`Success: ${result.message}`);
        loadTeams(); // Reload teams
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Failed to create sample teams:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const debugTeams = async () => {
    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${getApiBase()}/v2/cloud-run/debug/teams`, {
        headers: authHeaders,
        credentials: 'include'
      });
      
      const result = await response.json();
      console.log('Debug teams result:', result);
      
      if (response.ok) {
        alert(`Found ${result.count} teams in database:\n${result.teams.slice(0, 10).map(t => `- ${t.name} (${t.funder})`).join('\n')}${result.count > 10 ? `\n... and ${result.count - 10} more` : ''}`);
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Failed to debug teams:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const testTeamsAPI = async () => {
    try {
      const authHeaders = await getAuthHeaders();
      console.log('Testing Teams API with headers:', authHeaders);
      
      const url = `${getApiBase()}/v2/admin/teams?getAllTeams=true`;
      console.log('Calling URL:', url);
      
      const response = await fetch(url, {
        headers: authHeaders,
        credentials: 'include'
      });
      
      console.log('Response status:', response.status, response.statusText);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (response.ok) {
        const result = await response.json();
        console.log('Teams API success:', result);
        alert(`SUCCESS: Teams API returned ${result.teams?.length || 0} teams\nCheck console for full details`);
      } else {
        const errorText = await response.text();
        console.error('Teams API error response:', errorText);
        alert(`FAILED: Teams API returned ${response.status} ${response.statusText}\nError: ${errorText}\nCheck console for details`);
      }
    } catch (error) {
      console.error('Teams API exception:', error);
      alert(`EXCEPTION: ${error.message}\nCheck console for details`);
    }
  };

  const downloadTemplate = async () => {
    try {
      const url = `${getApiBase()}/v2/cloud-run/school/bulk-template`;
      
      // Create a temporary link to trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = 'school_bulk_processing_template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('Template download initiated');
    } catch (error) {
      console.error('Failed to download template:', error);
      alert(`Error downloading template: ${error.message}`);
    }
  };

  const renderExecutionPlan = (executionPlan) => {
    if (!executionPlan || executionPlan.length === 0) return null;

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          <AccountTreeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Execution Plan
        </Typography>
        {executionPlan.map((stage, index) => (
          <Box key={index} sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Stage {index + 1}: {stage.join(', ')}
              {stage.length > 1 && (
                <Chip 
                  label="Concurrent" 
                  size="small" 
                  color="primary" 
                  sx={{ ml: 1 }} 
                />
              )}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const renderServiceResult = (serviceKey, result) => {
    const service = availableServices.find(s => s.key === serviceKey);
    const isSuccess = result?.success;
    const isSkipped = result?.skipped;

    return (
      <Box key={serviceKey} sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {isSuccess ? (
            <CheckCircleIcon color="success" />
          ) : (
            <ErrorIcon color="error" />
          )}
          <Typography variant="h6">{service?.label}</Typography>
          {isSkipped && <Chip label="Skipped" size="small" />}
          {result?.assignment_created && <Chip label="Team Created" size="small" color="success" />}
        </Box>
        
        {result?.error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {result.error}
          </Alert>
        )}
        
        {result?.team_name && (
          <Typography variant="body2" color="text.secondary">
            Team: {result.team_name}
          </Typography>
        )}
        
        {result?.data_summary && (
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {JSON.stringify(result.data_summary, null, 2)}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderSingleProcessing = () => (
    <Stack spacing={3}>
      {/* NCES ID Input */}
      <TextField
        label="NCES School ID"
        value={ncessId}
        onChange={(e) => setNcessId(e.target.value)}
        placeholder="123456789012"
        fullWidth
        disabled={isProcessing}
        helperText="11 or 12-digit NCES identifier (leading zero will be added if needed)"
      />

      {/* Team Selection */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Team Assignment
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={isProcessing || !servicesToRun.team_assignment}>
              <InputLabel>Select Team</InputLabel>
              <Select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                label="Select Team"
              >
                <MenuItem value="">
                  <em>No team assignment</em>
                </MenuItem>
                {teamsLoading ? (
                  <MenuItem disabled>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Loading teams...
                  </MenuItem>
                ) : availableTeams.length > 0 ? (
                  availableTeams.map((team) => (
                    <MenuItem key={team.id} value={team.name}>
                      <GroupIcon sx={{ mr: 1 }} />
                      {team.name} ({team.school_count} schools)
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    <em>No teams available - create one below</em>
                  </MenuItem>
                )}
                <MenuItem value="new">
                  <AddIcon sx={{ mr: 1 }} />
                  Create New Team
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {selectedTeam === 'new' && (
            <Grid item xs={12} md={6}>
              <TextField
                label="New Team Name"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                fullWidth
                disabled={isProcessing}
                placeholder="Enter team name"
              />
            </Grid>
          )}
        </Grid>
        
        <FormControlLabel
          control={
            <Checkbox
              checked={createTeamIfMissing}
              onChange={(e) => setCreateTeamIfMissing(e.target.checked)}
              disabled={isProcessing}
            />
          }
          label="Create team if it doesn't exist"
        />
      </Box>

      {/* Service Selection with Dependencies */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Services to Run
        </Typography>
        <FormGroup>
          {availableServices.map((service) => (
            <FormControlLabel
              key={service.key}
              control={
                <Checkbox
                  checked={servicesToRun[service.key]}
                  onChange={() => handleServiceToggle(service.key)}
                  disabled={isProcessing}
                />
              }
              label={
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1">{service.label}</Typography>
                    {service.dependencies.length > 0 && (
                      <Tooltip title={`Depends on: ${service.dependencies.join(', ')}`}>
                        <InfoIcon fontSize="small" color="action" />
                      </Tooltip>
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {service.description}
                  </Typography>
                </Box>
              }
            />
          ))}
        </FormGroup>
      </Box>

      {/* Processing Options */}
      <FormControlLabel
        control={
          <Checkbox
            checked={forceReprocess}
            onChange={(e) => setForceReprocess(e.target.checked)}
            disabled={isProcessing}
          />
        }
        label="Force reprocess (run even if data already exists)"
      />

      {/* Process Button */}
      <Button
        variant="contained"
        onClick={handleProcessStandard}
        disabled={isProcessing || !user || !ncessId.trim() || Object.values(servicesToRun).every(v => !v)}
        startIcon={isProcessing ? <CircularProgress size={20} /> : <PlayArrowIcon />}
        size="large"
      >
        {isProcessing ? 'Processing...' : 'Process School'}
      </Button>

      {/* Results Display */}
      {processingResults && (
        <Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Processing Results
          </Typography>

          <Alert 
            severity={processingResults.overall_success ? 'success' : 'error'} 
            sx={{ mb: 2 }}
          >
            {processingResults.overall_success ? 
              'Processing completed successfully!' : 
              `Processing failed: ${processingResults.error || 'Unknown error'}`
            }
          </Alert>

          {processingResults.school_uuid && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2"><strong>School UUID:</strong> {processingResults.school_uuid}</Typography>
              <Typography variant="body2"><strong>School Name:</strong> {processingResults.school_name}</Typography>
              <Typography variant="body2"><strong>Team:</strong> {processingResults.team_name || 'None'}</Typography>
              <Typography variant="body2"><strong>Completed:</strong> {new Date(processingResults.processing_completed_at).toLocaleString()}</Typography>

              {/* AI Summary background status */}
              {aiSummaryStatus && (
                <Box
                  sx={{
                    mt: 1.5, p: 1.5,
                    bgcolor: aiSummaryStatus === 'complete' ? 'success.50' : 'grey.100',
                    borderRadius: 1,
                    display: 'flex', alignItems: 'flex-start', gap: 1.5
                  }}
                >
                  {aiSummaryStatus === 'generating' && (
                    <>
                      <CircularProgress size={18} sx={{ mt: 0.2, flexShrink: 0 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          AI Summaries generating in background
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          All 12 summaries (4 types × 3 drive times) are being generated. This typically takes 5–10 minutes. This page will update automatically.
                        </Typography>
                      </Box>
                    </>
                  )}
                  {aiSummaryStatus === 'complete' && (
                    <>
                      <CheckCircleIcon color="success" sx={{ fontSize: 20, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
                        AI Summaries ready — visible in the AI Summary tool
                      </Typography>
                    </>
                  )}
                  {aiSummaryStatus === 'timeout' && (
                    <Typography variant="body2" color="text.secondary">
                      AI Summaries are still generating. Check the AI Summary tool in a few minutes.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {processingResults.execution_plan && renderExecutionPlan(processingResults.execution_plan)}

          {processingResults.service_results && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Service Results</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {Object.entries(processingResults.service_results).map(([serviceKey, result]) => 
                  renderServiceResult(serviceKey, result)
                )}
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      )}
    </Stack>
  );

  const renderBulkProcessing = () => (
    <Stack spacing={3}>
      {/* File Upload */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Excel File Upload
        </Typography>
        
        {/* Download Template Button */}
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            onClick={downloadTemplate}
            startIcon={<DownloadIcon />}
            disabled={isBulkProcessing}
            size="medium"
            sx={{ mr: 2 }}
          >
            Download Template
          </Button>
          <Typography variant="body2" color="text.secondary" display="inline">
            Start with our CSV template with example data
          </Typography>
        </Box>

        {/* File Upload */}
        <input
          accept=".csv,.xlsx,.xls"
          style={{ display: 'none' }}
          id="bulk-file-upload"
          type="file"
          onChange={handleFileUpload}
          disabled={isBulkProcessing}
        />
        <label htmlFor="bulk-file-upload">
          <Button
            variant="contained"
            component="span"
            startIcon={<UploadIcon />}
            disabled={isBulkProcessing}
            size="large"
          >
            Upload Your File
          </Button>
        </label>
        <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>File Requirements:</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div">
            • CSV file with columns: <code>NCES_ID</code>, <code>Team_Name</code><br/>
            • NCES IDs: 11 or 12 digits (leading zero added automatically)<br/>
            • Team Names: Must match existing teams or will be created if enabled<br/>
            • Example: <code>123456789012, Elementary Team</code>
          </Typography>
        </Box>
      </Box>

      {/* File Preview */}
      {bulkData.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            File Preview ({bulkData.length} schools)
          </Typography>
          <Paper sx={{ maxHeight: 200, overflow: 'auto', p: 1 }}>
            {bulkData.slice(0, 10).map((school, index) => (
              <Typography key={index} variant="body2">
                {school.nces_id} → {school.team_name}
              </Typography>
            ))}
            {bulkData.length > 10 && (
              <Typography variant="body2" color="text.secondary">
                ... and {bulkData.length - 10} more
              </Typography>
            )}
          </Paper>
        </Box>
      )}

      {/* Processing Options */}
      <FormControlLabel
        control={
          <Checkbox
            checked={forceReprocess}
            onChange={(e) => setForceReprocess(e.target.checked)}
            disabled={isBulkProcessing}
          />
        }
        label="Force reprocess existing data"
      />
      
      <FormControlLabel
        control={
          <Checkbox
            checked={createTeamIfMissing}
            onChange={(e) => setCreateTeamIfMissing(e.target.checked)}
            disabled={isBulkProcessing}
          />
        }
        label="Create teams if they don't exist"
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={bulkRunAiSummaries}
            onChange={(e) => setBulkRunAiSummaries(e.target.checked)}
            disabled={isBulkProcessing}
          />
        }
        label="Run AI summaries (runs after main processing, 5 at a time)"
      />

      {/* Process Button */}
      <Button
        variant="contained"
        onClick={handleBulkProcess}
        disabled={isBulkProcessing || !user || bulkData.length === 0 || bulkData.length > MAX_BULK_SCHOOLS}
        startIcon={!bulkJobId && isBulkProcessing ? <CircularProgress size={20} /> : <PlayArrowIcon />}
        size="large"
      >
        {bulkData.length > MAX_BULK_SCHOOLS
          ? `Too many schools (max ${MAX_BULK_SCHOOLS})`
          : isBulkProcessing && bulkJobId
            ? `Processing… (${bulkJobStatus?.progress?.completed ?? 0}/${bulkJobStatus?.progress?.total_schools ?? bulkData.length})`
            : `Process ${bulkData.length} Schools`}
      </Button>

      {bulkData.length > 0 && bulkData.length <= MAX_BULK_SCHOOLS && (
        <Typography variant="caption" color="text.secondary">
          Max {MAX_BULK_SCHOOLS} schools per job. Batches of 5.
        </Typography>
      )}

      {/* Job Progress - show as soon as we have a job ID so the bar is visible while status loads */}
      {bulkJobId && (
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Job progress
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {bulkJobStatus ? (
                  <>
                    <Chip
                      label={(bulkJobStatus.status || '').toUpperCase()}
                      color={bulkJobStatus.status === 'completed' ? 'success' : bulkJobStatus.status === 'failed' ? 'error' : 'primary'}
                      size="small"
                    />
                    {['created', 'processing'].includes(bulkJobStatus.status) && (
                      <Button size="small" color="error" startIcon={<CancelIcon />} onClick={cancelBulkJob}>
                        Cancel
                      </Button>
                    )}
                  </>
                ) : (
                  <Chip label="LOADING…" color="default" size="small" />
                )}
              </Box>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2">
                {bulkJobStatus
                  ? `${bulkJobStatus.progress?.completed ?? 0} / ${bulkJobStatus.progress?.total_schools ?? 0} schools`
                  : 'Loading status…'}
              </Typography>
              <LinearProgress
                variant={bulkJobStatus?.progress?.total_schools > 0 ? 'determinate' : 'indeterminate'}
                value={
                  bulkJobStatus?.progress?.total_schools > 0
                    ? (bulkJobStatus.progress.completed / bulkJobStatus.progress.total_schools) * 100
                    : 0
                }
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="success.main">
                Completed: {bulkJobStatus?.progress?.completed ?? 0}
              </Typography>
              <Typography variant="body2" color="error.main">
                Failed: {bulkJobStatus?.progress?.failed ?? 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                In progress: {bulkJobStatus?.progress?.in_progress ?? 0}
              </Typography>
            </Box>
            {bulkSchoolRows.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  By school
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>NCES ID</TableCell>
                        <TableCell>Team</TableCell>
                        <TableCell>Main processing</TableCell>
                        {bulkJobStatus?.ai_summaries_job_id && <TableCell>AI summaries</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bulkSchoolRows.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.nces_id}</TableCell>
                          <TableCell>{row.team_name}</TableCell>
                          <TableCell>
                            {row.mainStatus === 'completed' && <Chip size="small" label="Completed" color="success" />}
                            {row.mainStatus === 'failed' && (
                              <Tooltip title={row.mainError || 'Failed'}>
                                <Chip size="small" label="Failed" color="error" />
                              </Tooltip>
                            )}
                            {(row.mainStatus === 'in_progress' || row.mainStatus === 'pending') && (
                              <Chip size="small" label={row.mainStatus === 'in_progress' ? 'In progress' : 'Pending'} color="default" />
                            )}
                          </TableCell>
                          {bulkJobStatus?.ai_summaries_job_id && (
                            <TableCell>
                              {row.aiStatus === 'completed' && <Chip size="small" label="Completed" color="success" />}
                              {row.aiStatus === 'failed' && <Chip size="small" label="Failed" color="error" />}
                              {row.aiStatus === 'in_progress' && <Chip size="small" label="In progress" color="primary" />}
                              {row.aiStatus === 'pending' && <Chip size="small" label="Pending" color="default" />}
                              {row.aiStatus == null && <Typography variant="caption" color="text.secondary">—</Typography>}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
            {bulkJobStatus?.ai_summaries_job_id && (
              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  AI summaries (separate step)
                </Typography>
                {bulkAiSummariesJobStatus ? (
                  <>
                    <Typography variant="body2">
                      {bulkAiSummariesJobStatus.progress?.completed ?? 0} / {bulkAiSummariesJobStatus.progress?.total_schools ?? 0} schools
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={
                        (bulkAiSummariesJobStatus.progress?.total_schools > 0
                          ? (bulkAiSummariesJobStatus.progress.completed / bulkAiSummariesJobStatus.progress.total_schools) * 100
                          : 0)
                      }
                      sx={{ height: 6, borderRadius: 1 }}
                      color="secondary"
                    />
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Starting…
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Bulk Results */}
      {bulkResults && (
        <Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Bulk Processing Results
          </Typography>

          <Alert 
            severity={bulkResults.overall_success ? 'success' : 'warning'} 
            sx={{ mb: 2 }}
          >
            {bulkResults.overall_success ? 
              'All schools processed successfully!' : 
              `Processing completed with some issues. Check details below.`
            }
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="success.main">Processed</Typography>
                  <Typography variant="h4">{bulkResults.schools_processed?.length || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="error.main">Failed</Typography>
                  <Typography variant="h4">{bulkResults.schools_failed?.length || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" color="warning.main">Validation Errors</Typography>
                  <Typography variant="h4">{bulkResults.validation_errors?.length || 0}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}
    </Stack>
  );

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <SchoolIcon color="primary" />
          <Typography variant="h5">Enhanced Standard NCES School Processing</Typography>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Process schools through all required Cloud Run services with proper dependency management,
          team assignments, and bulk processing capabilities. Services run in optimal order with
          concurrent execution where possible.
        </Typography>

        {!user && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            You must be logged in to process schools.
          </Alert>
        )}

        {/* Debug Section - only show if no teams are available */}
        {availableTeams.length === 0 && !teamsLoading && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" gutterBottom>
              <strong>No teams found.</strong> You may need to create some teams first.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={createSampleTeams}
                disabled={isProcessing}
              >
                Create Sample Teams
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={debugTeams}
                disabled={isProcessing}
              >
                Debug Teams
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={testTeamsAPI}
                disabled={isProcessing}
              >
                Test Teams API
              </Button>
            </Box>
          </Alert>
        )}

        {/* Mode Selection */}
        <Tabs
          value={bulkMode ? 1 : 0}
          onChange={(e, newValue) => setBulkMode(newValue === 1)}
          sx={{ mb: 3 }}
        >
          <Tab label="Single School" />
          <Tab label="Bulk Processing" />
        </Tabs>

        {/* Content based on mode */}
        {bulkMode ? renderBulkProcessing() : renderSingleProcessing()}
      </CardContent>
    </Card>
  );
};

export default EnhancedStandardProcessor; 